// Schemes we allow on user-authored links.
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
// Schemes that can execute or smuggle script — always rejected. Checked on the
// raw string so case / whitespace tricks (`JavaScript:`, ` data:`) can't slip past.
const DANGEROUS_SCHEME = /^\s*(javascript|data|vbscript|file|blob):/i;

/**
 * Returns a safe href for a user-supplied link URL, or '' when the URL uses a
 * disallowed scheme (e.g. `javascript:`). Scheme-less input — including bare
 * `host:port` like `localhost:3000`, which the URL parser mis-reads as a
 * protocol — is upgraded to an https web address.
 *
 * Idempotent: sanitizing an already-sanitized value returns it unchanged, which
 * keeps the link node transform from looping.
 */
export function sanitizeLinkUrl(url: string): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return '';
  if (DANGEROUS_SCHEME.test(trimmed)) return '';

  try {
    const parsed = new URL(trimmed);
    if (SAFE_PROTOCOLS.has(parsed.protocol)) return trimmed;
    // Parsed with a non-safe protocol. A real authority (host present) means an
    // unsupported scheme like `ftp://` — reject it. No authority means the URL
    // parser mis-read a scheme-less `host:port` (e.g. `localhost:3000`) as a
    // protocol, so treat it as a bare web address.
    return parsed.host ? '' : `https://${trimmed.replace(/^\/+/, '')}`;
  } catch {
    // No parseable scheme — assume a bare web address.
    return `https://${trimmed.replace(/^\/+/, '')}`;
  }
}
